import * as THREE from 'three'

interface LevelMeta {
  z: number
  xMin: number
  yMin: number
  tileMeters: number
  centerFx: number
  centerFy: number
  tiles: [number, number, number][]
}

interface Manifest {
  attribution: string
  grid: number
  levels: LevelMeta[]
}

/**
 * 航空写真タイル（現行ソース: 地理院タイル・出典表記必須）。
 * ズーム段ごとに8×8のタイル平面グリッドを構築し、段間クロスフェードで
 * 「泰山荘→関東→日本」のズームアウトを表現する。
 * 将来 Google Photorealistic 3D Tiles へ差し替える場合はこのクラスを置換する。
 */
export class AerialTiles {
  readonly root = new THREE.Group()
  /** 1ワールドユニット = 何メートルか（Proto10 は 4m/unit） */
  metersPerUnit = 4
  attribution = ''
  private levels = new Map<number, { group: THREE.Group; mats: THREE.MeshBasicMaterial[]; meta: LevelMeta }>()
  private loaded = false

  get isLoaded(): boolean {
    return this.loaded
  }

  get zooms(): number[] {
    return [...this.levels.keys()].sort((a, b) => b - a)
  }

  async load(baseUrl = './assets/gsi'): Promise<void> {
    if (this.loaded) return
    const manifest: Manifest = await fetch(`${baseUrl}/manifest.json`).then((r) => r.json())
    this.attribution = manifest.attribution
    const loader = new THREE.TextureLoader()

    // 大きい段（z小）ほど下に置いてZファイトと描画順を回避
    const yByZ: Record<number, number> = { 17: 0, 14: -3, 11: -14, 8: -60, 7: -110, 6: -180 }

    const jobs: Promise<void>[] = []
    for (const meta of manifest.levels) {
      const group = new THREE.Group()
      const mats: THREE.MeshBasicMaterial[] = []
      const tileU = meta.tileMeters / this.metersPerUnit // タイル1枚のワールドサイズ
      for (const [x, y, ok] of meta.tiles) {
        const mat = new THREE.MeshBasicMaterial({
          color: ok ? 0xffffff : 0x1a222c,
          transparent: true,
          opacity: 1,
          depthWrite: false,
        })
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(tileU, tileU), mat)
        mesh.rotation.x = -Math.PI / 2
        // 中心座標(centerFx/Fy)がワールド原点に来るよう配置
        const cx = (x + 0.5 - meta.centerFx) * tileU
        const cz = (y + 0.5 - meta.centerFy) * tileU
        mesh.position.set(cx, yByZ[meta.z] ?? 0, cz)
        group.add(mesh)
        mats.push(mat)
        if (ok) {
          jobs.push(
            loader.loadAsync(`${baseUrl}/${meta.z}/${x}_${y}.jpg`).then((tex) => {
              tex.colorSpace = THREE.SRGBColorSpace
              tex.anisotropy = 8
              mat.map = tex
              mat.needsUpdate = true
            }).catch(() => void 0),
          )
        }
      }
      group.renderOrder = -meta.z // 広域を先に描く
      this.levels.set(meta.z, { group, mats, meta })
      this.root.add(group)
    }
    await Promise.all(jobs)
    this.loaded = true
  }

  setLevelOpacity(z: number, o: number): void {
    const l = this.levels.get(z)
    if (!l) return
    const clamped = Math.max(0, Math.min(1, o))
    l.group.visible = clamped > 0.01
    for (const m of l.mats) m.opacity = clamped
  }

  setAllOpacity(o: number): void {
    for (const z of this.levels.keys()) this.setLevelOpacity(z, o)
  }

  /** 高度(ワールドu)に応じた段クロスフェードの既定カーブ（z17→…→z6=列島全域） */
  updateForAltitude(alt: number): void {
    // 各段が主役になる高度帯（unit）。頂上(640)までに z8→z6 で「正しい日本列島」が開ける
    const bands: [number, number, number][] = [
      [17, 0, 90],
      [14, 45, 260],
      [11, 160, 460],
      [8, 320, 700],
      [7, 460, 999999], // 列島全域を2倍解像度で（16×16）
      [6, 460, 999999], // z7の下敷き（縁の保険）
    ]
    for (const [z, lo, hi] of bands) {
      let o = 1
      const fadeIn = lo === 0 ? 1 : Math.min(1, Math.max(0, (alt - lo) / (lo * 0.6)))
      const fadeOut = hi > 99999 ? 1 : Math.min(1, Math.max(0, (hi - alt) / (hi * 0.35)))
      o = Math.min(fadeIn, fadeOut)
      this.setLevelOpacity(z, o)
    }
  }
}
